import { ReactElement, useCallback, useMemo, useState } from 'react'
import { find, keyBy } from 'lodash-es'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DenseTable, Section } from '@ui2/structure'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useResultsStore, useSystemManagerStore } from '@editorStores'
import { ConnectorSelection } from '../../connectorSelection'
import useSaveAnchorManualLoads, {
  AnchorEdit,
  AnchorEdits,
} from '../../hooks/useSaveAnchorManualLoads'
import { loadSources } from '../AnchorDataGrid2/constants'
import ConnectorSelectionSelect from '../ConnectorSelectionSelect'
import { formatNewtonsAsKn, tryFormulaToNumber } from '../Results2AnchorDetailPane/utils'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'

interface Props {
  /** The anchors selected in the list — the rows every action below is applied to. */
  selectedItems: Results2ListItem[]
  /** Disables the actions while a list autosave is dirty or in flight. */
  disableForm?: boolean
}

// "Wand über Eck" needs a wall picked in the scene per anchor, so it stays single-anchor only.
const sourceOptions = Object.entries(loadSources).filter(
  ([key]) => key !== 'empty' && key !== 'wallCorner',
)

/**
 * Multi-edit pane of the Results2 "Anker" tab, shown while several anchors are selected: adds one
 * manual load to all of them, sets (or appends to) their comment and sets their Zuganker. Whatever is filled in is
 * applied together in a single save, which replays the saved rows of the untouched anchors — so
 * it behaves exactly like editing each anchor in the single-anchor pane. What those anchors carry
 * today is listed at the bottom, so the effect of an edit is visible before applying it.
 */
const Results2AnchorMultiEditPane = ({
  selectedItems,
  disableForm = false,
}: Props): ReactElement => {
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const anchorChecks = useResultsStore(state => state.anchorChecks)
  const connections = useSystemManagerStore(state => state.connections)
  const { saveAnchorEdits, isSaving, isComputingAnchorChecks } = useSaveAnchorManualLoads()

  const [source, setSource] = useState('')
  const [formula, setFormula] = useState('')
  const [comment, setComment] = useState('')
  const [appendComment, setAppendComment] = useState(false)
  // `null` keeps each anchor's Zuganker.
  const [connectorSelection, setConnectorSelection] = useState<ConnectorSelection | null>(null)

  const busy = isSaving || isComputingAnchorChecks || disableForm

  const rowsBySupport = useMemo(() => {
    const rows: Record<string, AnchorInterventionsTableRowData | undefined> = {}
    for (const item of selectedItems) {
      rows[item.id] = find(anchorInterventionTableData ?? [], { support_guid: item.id })
    }
    return rows
  }, [anchorInterventionTableData, selectedItems])

  const loadValue = tryFormulaToNumber(formula)
  const formulaInvalid = !!formula.trim() && loadValue == null
  // A row is only sent once source and formula are both there; half of one is an error, not a
  // reason to silently skip it.
  const loadRowIncomplete = !!source !== !!formula.trim()
  const hasLoad = !!source && !!formula.trim() && loadValue != null
  const hasComment = !!comment.trim()
  const hasConnectorSelection = connectorSelection !== null
  // Only wall anchors are chosen from the prioritization (and so can be fixed by hand).
  const anchorsWithoutConnectorChoice = selectedItems.filter(
    item => !rowsBySupport[item.id]?.segment_guid,
  ).length
  const canApply =
    !busy &&
    !formulaInvalid &&
    !loadRowIncomplete &&
    (hasLoad || hasComment || hasConnectorSelection)

  const checkBySupport = useMemo(
    () => keyBy(anchorChecks ?? [], check => check.element_guid),
    [anchorChecks],
  )

  /** Applies whatever is filled in — load and comment together, in one save. */
  const handleApply = useCallback(() => {
    const edits: AnchorEdits = {}
    for (const item of selectedItems) {
      const row = rowsBySupport[item.id]
      if (!row) continue
      const edit: AnchorEdit = {}
      if (hasLoad) {
        edit.manualLoads = [
          ...row.manual_loads,
          { load: (tryFormulaToNumber(formula) ?? 0) * 1000, load_formula: formula, source },
        ]
      }
      if (hasComment) {
        edit.comment = appendComment && row.comment ? `${row.comment} ${comment}` : comment
      }
      if (connectorSelection && row.segment_guid) {
        edit.connectorSelection = connectorSelection
      }
      edits[item.id] = edit
    }
    saveAnchorEdits(edits)
    setSource('')
    setFormula('')
    setComment('')
    setConnectorSelection(null)
  }, [
    appendComment,
    comment,
    connectorSelection,
    formula,
    hasComment,
    hasLoad,
    rowsBySupport,
    saveAnchorEdits,
    selectedItems,
    source,
  ])

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[{ id: 'multi-edit', label: 'Mehrfachbearbeitung' }]}
          value="multi-edit"
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>

      <Results2PaneHeader
        title={`${selectedItems.length} Anker`}
        subtitle="Änderungen gelten für alle ausgewählten Anker"
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
        <Stack spacing={2.5} sx={{ py: 1.5 }}>
          <Section title="Manuelle Last hinzufügen" hideBorderBottom>
            <Stack spacing={1} sx={{ pb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Select
                  size="small"
                  value={source}
                  onChange={event => setSource(event.target.value)}
                  displayEmpty
                  disabled={busy}
                  sx={{ width: 170, flexShrink: 0, fontSize: 13 }}
                  data-cy="anchor-multi-manual-source"
                >
                  <MenuItem value="">
                    <em>Herkunft wählen …</em>
                  </MenuItem>
                  {sourceOptions.map(([key, label]) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  size="small"
                  value={formula}
                  onChange={event => setFormula(event.target.value)}
                  placeholder="Formel oder Wert in kN, z. B. =12*0,35"
                  disabled={busy}
                  error={formulaInvalid}
                  sx={{ flex: 1, '& input': { fontFamily: 'monospace', fontSize: 13 } }}
                  data-cy="anchor-multi-manual-load"
                />

                <Box
                  sx={{
                    width: 90,
                    flexShrink: 0,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 13,
                    color: formulaInvalid ? 'error.main' : 'text.secondary',
                  }}
                >
                  {formulaInvalid
                    ? 'Fehler'
                    : loadValue == null
                    ? '–'
                    : `${formatNewtonsAsKn(loadValue * 1000)} kN`}
                </Box>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Die Last wird als zusätzliche Zeile ergänzt; bestehende manuelle Lasten bleiben
                erhalten. „Wand über Eck“ muss je Anker einzeln ermittelt werden.
              </Typography>
            </Stack>
          </Section>

          <Section title="Kommentar" hideBorderBottom>
            <Stack spacing={1} sx={{ pb: 1 }}>
              <TextField
                size="small"
                value={comment}
                onChange={event => setComment(event.target.value)}
                placeholder="Kommentar für alle ausgewählten Anker"
                disabled={busy}
                fullWidth
                data-cy="anchor-multi-comment-field"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={appendComment}
                    onChange={event => setAppendComment(event.target.checked)}
                    disabled={busy}
                  />
                }
                label={<Typography variant="body2">An bestehenden Kommentar anhängen</Typography>}
              />

              <Typography variant="caption" color="text.secondary">
                Ohne „anhängen“ wird der bestehende Kommentar ersetzt; ein leeres Feld lässt ihn
                unverändert.
              </Typography>
            </Stack>
          </Section>

          <Section title="Zuganker festlegen" hideBorderBottom>
            <Stack spacing={1} sx={{ pb: 1 }}>
              <ConnectorSelectionSelect
                value={connectorSelection}
                onChange={setConnectorSelection}
                connections={connections}
                label="Zuganker"
                dataCy="anchor-connector-selection"
                disabled={busy}
                offerUnchanged
              />
              <Typography variant="caption" color="text.secondary">
                „Automatisch“ überlässt die Wahl wieder der Priorisierung. Ein festgelegter Zuganker
                wird auch nachgewiesen, wenn er nicht ausreicht.
                {anchorsWithoutConnectorChoice > 0 &&
                  ` ${anchorsWithoutConnectorChoice} der ausgewählten Anker sitzen an Stützen oder Rippen und behalten ihren Zuganker.`}
              </Typography>
            </Stack>
          </Section>

          <Section title="Ausgewählte Anker" hideBorderBottom>
            <Box sx={{ pb: 1 }}>
              <DenseTable
                headers={['Anker', 'Zuganker', 'Manuelle Lasten', 'Kommentar']}
                columnAlign={['left', 'left', 'left', 'left']}
                rows={selectedItems.map(item => {
                  const row = rowsBySupport[item.id]
                  const usedAnchor = checkBySupport[item.id]?.used_anchor
                  return [
                    item.label ?? item.id,
                    usedAnchor ? (
                      <Box sx={{ whiteSpace: 'nowrap' }}>
                        {usedAnchor.short_name}
                        {row?.connectorSelectionPolicy === 'UserDefined' &&
                        row.userDefinedConnectorId === usedAnchor.guid
                          ? ' (manuell)'
                          : ''}
                      </Box>
                    ) : (
                      '–'
                    ),
                    row?.manual_loads.length ? (
                      <Stack spacing={0.25}>
                        {row.manual_loads.map((manualLoad, index) => (
                          <Box key={index} sx={{ whiteSpace: 'nowrap' }}>
                            {manualLoad.source} · {formatNewtonsAsKn(manualLoad.load)} kN
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      '–'
                    ),
                    row?.comment ? (
                      <Tooltip title={row.comment} arrow disableInteractive>
                        <Box
                          sx={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.comment}
                        </Box>
                      </Tooltip>
                    ) : (
                      '–'
                    ),
                  ]
                })}
                emptyMessage="Keine Anker ausgewählt"
              />
            </Box>
          </Section>
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flexShrink: 0, px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}
      >
        <Typography variant="caption" color={formulaInvalid ? 'error.main' : 'text.secondary'}>
          {formulaInvalid
            ? 'Formel kann nicht gelesen werden'
            : loadRowIncomplete
            ? 'Herkunft und Wert der Last angeben'
            : hasLoad || hasComment || hasConnectorSelection
            ? `${[
                hasLoad ? 'Last' : undefined,
                hasComment ? 'Kommentar' : undefined,
                hasConnectorSelection ? 'Zuganker' : undefined,
              ]
                .filter(Boolean)
                .join(', ')} ${
                [hasLoad, hasComment, hasConnectorSelection].filter(Boolean).length > 1
                  ? 'werden'
                  : 'wird'
              } übernommen`
            : 'Nichts einzutragen'}
        </Typography>

        <LoadingButton
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={!canApply}
          loading={isSaving || isComputingAnchorChecks}
          data-cy="anchor-multi-apply-btn"
        >
          Auf {selectedItems.length} Anker anwenden
        </LoadingButton>
      </Stack>
    </Box>
  )
}

export default Results2AnchorMultiEditPane
